import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { PersonalizationVariable } from '@/lib/types/personalization-variable';
import { PERSONALIZATION_VARIABLES } from '@/lib/constants/personalization-variables';
import { cn } from '@/lib/utils';

interface VariablePickerProps {
  onSelect: (variable: PersonalizationVariable) => void;
  onClose: () => void;
  position?: { x: number; y: number };
  side?: 'top' | 'bottom';
  className?: string;
  query?: string;
}

export function VariablePicker({
  onSelect,
  onClose,
  position,
  side = 'bottom',
  className,
  query = '',
}: VariablePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredVariables = useMemo(() => {
    if (!query) return PERSONALIZATION_VARIABLES;
    const lowerQuery = query.toLowerCase();
    return PERSONALIZATION_VARIABLES.filter(
      (v) =>
        v.key.toLowerCase().includes(lowerQuery) ||
        v.label.toLowerCase().includes(lowerQuery)
    );
  }, [query]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Close if no matches
  useEffect(() => {
    if (filteredVariables.length === 0) {
      onClose();
    }
  }, [filteredVariables.length, onClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (filteredVariables.length === 0) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) =>
          prev < filteredVariables.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredVariables.length - 1
        );
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        onSelect(filteredVariables[selectedIndex]);
        break;
    }
  }, [onClose, onSelect, selectedIndex, filteredVariables]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose, handleKeyDown]);

  if (filteredVariables.length === 0) {
    return null;
  }

  const positionStyles = position
    ? {
        position: 'absolute' as const,
        left: `${position.x}px`,
        top: side === 'top' ? 'auto' : `${position.y}px`,
        bottom: side === 'top' ? `calc(100% - ${position.y}px + 8px)` : 'auto',
      }
    : {};

  return (
    <div
      ref={containerRef}
      className={cn(
        'z-50 min-w-[280px] max-w-[340px] bg-popover rounded-lg shadow-xl py-1',
        className
      )}
      style={positionStyles}
    >
      {filteredVariables.map((variable, index) => (
        <button
          key={variable.key}
          type="button"
          className={cn(
            'w-[calc(100%-8px)] mx-1 pl-3 pr-2 py-2 text-left text-sm rounded-md focus:outline-none',
            index === selectedIndex && 'bg-surface-subtle'
          )}
          onClick={() => onSelect(variable)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <span>{variable.label}</span>
          <span className="ml-2 text-content-subtle">{variable.placeholder}</span>
        </button>
      ))}
    </div>
  );
}
